import {
  InternalGetIngestionDocumentFragment,
  InternalIngestionFolderFragment,
  RuleType,
} from '@client/graphql/__generated__/types';

/**
 * Computes pipeline targeting's config (if needed by the pipeline, error rules, is required document)
 * Returns document-id and form level target maps keyed by contract document id.
 */
export function getPipelineTargetingByContract(
  folder: InternalIngestionFolderFragment | null | undefined,
  selectedDocument: InternalGetIngestionDocumentFragment | null | undefined
): {
  byDocumentId: Map<string, boolean>;
  byForm: Map<string, boolean>;
  byDocumentIdAndRequiredDocument: Map<string, boolean>;
  byDocumentIdAndErrorRulesCount: Map<string, number>;
} {
  const byDocumentId = new Map<string, boolean>();
  const byForm = new Map<string, boolean>();
  const byDocumentIdAndRequiredDocument = new Map<string, boolean>();
  const byDocumentIdAndErrorRulesCount = new Map<string, number>();

  if (!folder || !selectedDocument) {
    return {
      byDocumentId,
      byForm,
      byDocumentIdAndRequiredDocument,
      byDocumentIdAndErrorRulesCount,
    };
  }

  const stageKey = folder.stage;
  const stages = folder.transactionPipeline?.stages || [];
  const currentStage = stages.find((s) => s.stage === stageKey);
  const rules =
    // [NOTE] Target document could be a nested document, so we need to add the children as well.
    [
      ...(currentStage?.validationRules || []),
      ...(currentStage?.validationRules?.flatMap((vr) => vr.children) || []),
    ].map((vr) => ({
      isRequiredDocument: vr.isRequiredDocument,
      targetDocument: vr.targetDocument,
      targetDocumentRulesCount: vr.rules.filter(
        (r) => r.ruleType === RuleType.ERROR
      ).length,
    })) || [];

  const targetedDocIds = new Set<string>();
  const targetedDocForms = new Set<string>();
  const targetedDocErrorRulesCount = new Map<string, number>();
  const targetedDocRequiredDocument = new Map<string, boolean>();
  rules.forEach((vr) => {
    const td = vr?.targetDocument;
    if (td?.id) targetedDocIds.add(td.id);
    if (td?.form) targetedDocForms.add(td.form);
    if (vr.targetDocumentRulesCount)
      targetedDocErrorRulesCount.set(
        td?.id ?? td?.form ?? '',
        vr.targetDocumentRulesCount
      );
    if (vr.isRequiredDocument)
      targetedDocRequiredDocument.set(
        td?.id ?? td?.form ?? '',
        vr.isRequiredDocument
      );
  });

  (selectedDocument.contractDocuments || []).forEach((cd) => {
    const docId = cd.documentVersion?.document?.id;
    const form = cd.documentVersion?.document?.form;

    const isTargetDoc = docId ? targetedDocIds.has(docId) : false;
    const isTargetForm = form ? targetedDocForms.has(form) : false;
    byDocumentId.set(cd.id, isTargetDoc);
    byForm.set(cd.id, isTargetForm);
    byDocumentIdAndErrorRulesCount.set(
      cd.id,
      targetedDocErrorRulesCount.get(docId ?? form ?? '') ?? 0
    );
    byDocumentIdAndRequiredDocument.set(
      cd.id,
      targetedDocRequiredDocument.get(docId ?? form ?? '') ?? false
    );
  });

  return {
    byDocumentId,
    byForm,
    byDocumentIdAndRequiredDocument,
    byDocumentIdAndErrorRulesCount,
  };
}

/**
 * Counts RuleType.ERROR rules at the field level per contract document.
 */
export function getFieldErrorRuleCountByContract(
  selectedDocument: InternalGetIngestionDocumentFragment | null | undefined
): Map<string, number> {
  const map = new Map<string, number>();

  (selectedDocument?.contractDocuments || []).forEach((cd) => {
    const fields = cd.documentVersion?.documentVersionFields || [];
    const count = fields.reduce((acc, f) => {
      const defaultRules = f.field.defaultRules || [];
      const overrideRules = f.rules || [];
      const r = [...defaultRules, ...overrideRules];

      return acc + r.filter((rr) => rr.ruleType === RuleType.ERROR).length;
    }, 0);
    map.set(cd.id, count);
  });

  return map;
}
